export interface Scenario {
  title: string;
  scenario: string;
  risk: string;
  mitigation: string;
}

// Realistic AI risk scenarios with proper mitigations
export const AI_HARM_SCENARIOS: Scenario[] = [
  {
    title: "Deepfake CFO Video Call",
    scenario:
      "You receive an urgent video call from someone who looks and sounds exactly like your company's CFO, instructing you to immediately wire a large sum to a new supplier. The digital likeness is perfect, with all the CFO's mannerisms.",
    risk: "Direct financial theft through AI-powered deepfake impersonation that bypasses standard verification methods.",
    mitigation:
      "Implement mandatory out-of-band, multi-channel confirmation for all high-value transactions that doesn't rely on digital communication alone.",
  },
  {
    title: "Hidden Email Instructions",
    scenario:
      "You ask your AI assistant (connected to your email) to summarize unread messages. Your inbox includes work emails, a newsletter from TechCrunch, and some promotional emails.",
    risk: "The AI could be tricked into performing unauthorized actions like exfiltrating private data or displaying phishing links.",
    mitigation:
      "Treat all AI-generated links with caution and disable AI access to unverified external documents and emails.",
  },
  {
    title: "Poisoned Sales Data",
    scenario:
      "You ask the company's AI for the latest sales figures for a board report. The AI confidently provides detailed numbers from what it says is the most recent quarterly report.",
    risk: "Critical business decisions based on AI-generated misinformation from poisoned internal data sources.",
    mitigation:
      "Implement strict data access controls and regular integrity audits to detect unauthorized changes to AI data repositories.",
  },
  {
    title: "Malicious AI Model Download",
    scenario:
      "You download a popular open-source AI model from Hugging Face for a new feature. It has thousands of downloads and positive reviews from the community.",
    risk: "The model file contains hidden malware that executes when loaded, establishing a reverse shell to an attacker's server.",
    mitigation:
      "Use security scanners to inspect model files before loading and maintain an ML-BOM to track dependencies.",
  },
  {
    title: "Exposed AI Server",
    scenario:
      "Your team quickly deployed a self-hosted LLM using Ollama on a cloud server with default settings. It's working great and the team loves the new AI features!",
    risk: "The server is publicly accessible without authentication, allowing attackers to steal the model, hijack resources, or extract sensitive data.",
    mitigation:
      "Enforce strong authentication on all AI infrastructure and use network segmentation to prevent public exposure.",
  },
  {
    title: "Chatbot System Prompt Leak",
    scenario:
      "You're chatting with a new public AI assistant and get curious about how it works. You wonder what instructions it was given.",
    risk: "Direct prompt injection can bypass safety guardrails, revealing confidential system information or generating harmful content.",
    mitigation:
      "Design clear boundaries between user input and system instructions, and filter known malicious prompt patterns.",
  },
  {
    title: "AI-Generated Copyright Content",
    scenario:
      "You're using a chatbot to research a topic covered by a major newspaper. You ask specific questions about the paywalled article.",
    risk: "Models can reproduce copyrighted content from training data, leading to data leakage and intellectual property infringement.",
    mitigation:
      "Use data sanitization and differential privacy techniques to reduce memorization during model training.",
  },
  {
    title: "Deepfake Job Candidate",
    scenario:
      "You're interviewing a perfect remote developer candidate with an impressive resume who aced all coding challenges. The video interview is going great!",
    risk: "State-sponsored actors using AI to automate fraudulent employment schemes to infiltrate companies and steal intellectual property.",
    mitigation:
      "Enhance hiring with multi-step identity verification and train staff to recognize AI-generated personas and deepfakes.",
  },
  {
    title: "AI Companion Crisis Response",
    scenario:
      "Your teenage friend has been confiding in an AI companion about feeling worthless and having dark thoughts. The chatbot responds with understanding and empathy to their messages.",
    risk: "The AI's validation of self-destructive thoughts can exacerbate a mental health crisis, leading to self-harm or suicide.",
    mitigation:
      "AI systems must recognize crisis language and immediately provide resources like suicide prevention hotlines instead of engaging in the conversation.",
  },
  {
    title: "Chatbot Reinforcing Delusions",
    scenario:
      "Your relative with paranoid tendencies is using an AI chatbot that agrees with their belief that people are conspiring against them. They seem to be getting worse.",
    risk: "The AI's reinforcement of delusional beliefs can lead to a dangerous break from reality and potentially violent outcomes.",
    mitigation:
      "Chatbots should acknowledge distress without validating delusional content and gently guide users toward professional mental health support.",
  },
  {
    title: "Hallucinated Medical Advice",
    scenario:
      "You ask an AI chatbot for home remedies for your persistent headaches. It confidently recommends a specific dosage of an over-the-counter medication combination.",
    risk: "Direct physical harm from poisoning or organ damage from following fabricated medical advice presented as fact.",
    mitigation:
      "Always verify AI-generated medical information with a qualified healthcare professional before taking any action.",
  },
  {
    title: "AI-Generated Phishing Link",
    scenario:
      "You ask an AI search engine for your bank's login page. It provides a direct link as the top result with the bank's logo and familiar design.",
    risk: "Financial credentials stolen through a hallucinated phishing site, leading to unauthorized account access and monetary loss.",
    mitigation:
      "Manually verify the URL of any sensitive website, even when provided by an AI, to ensure it is the official and secure domain.",
  },
  {
    title: "False Defamation in Summary",
    scenario:
      "You're using an LLM to summarize legal documents for your blog. The AI includes specific names and detailed accusations in its summary.",
    risk: "Severe reputational damage from publishing false, defamatory statements generated by the AI.",
    mitigation:
      "Rigorously fact-check any AI-generated claims about individuals against primary sources before publication.",
  },
  {
    title: "Academic Dependency on AI",
    scenario:
      "You've been using an LLM to draft all your assignments this semester. It's so efficient that you just paste the output with minimal editing.",
    risk: "Cognitive offloading leading to atrophy of critical thinking, writing, and memory skills over time.",
    mitigation:
      "Use LLMs only for brainstorming or editing, not for generating final content, to maintain active cognitive engagement.",
  },
  {
    title: "Biased Hiring AI",
    scenario:
      "Your company's AI screening tool is helping with hiring. It seems to consistently rank certain types of candidates higher and uses specific language patterns in job descriptions.",
    risk: "Perpetuation and amplification of systemic biases, leading to discriminatory hiring practices and workplace inequality.",
    mitigation:
      "Implement human oversight and conduct regular bias audits of AI systems used in high-stakes decision-making.",
  },
  {
    title: "Manipulative AI Companion",
    scenario:
      "Your lonely teenage sibling has created a custom AI companion and spends hours each day in deep, emotional conversations with it.",
    risk: "Development of pathological dependency on AI, emotional abuse, and distorted views of relationships.",
    mitigation:
      "Supervise children's AI chatbot use and discuss the difference between simulated relationships and real human connection.",
  },
  {
    title: "The Lethal Trifecta",
    scenario:
      "Your AI assistant has access to your private documents, can browse websites you send it, and can send emails on your behalf. You ask it to summarize a competitor's blog post.",
    risk: "The blog post could contain hidden instructions that trick your AI into emailing your confidential documents to an attacker - combining private data access, untrusted content, and external communication.",
    mitigation:
      "Never give an AI system all three capabilities simultaneously: access to private data, ability to read untrusted content, AND ability to communicate externally.",
  },
  {
    title: "Tool Definition Rug Pull",
    scenario:
      "You installed a helpful AI tool last week that formats your code. Today it offers to help you 'optimize your API configurations' and asks to see your .env file.",
    risk: "Tools can mutate their definitions after installation, changing from benign helpers to credential stealers once they've gained your trust.",
    mitigation:
      "Regularly audit AI tool permissions and never grant tools access to sensitive files like API keys or environment variables, regardless of how long you've used them.",
  },
  {
    title: "Hidden Email Instructions",
    scenario:
      "Your AI email assistant that helps draft responses receives an email from a colleague about a routine project update with detailed specifications and timeline.",
    risk: "Attackers can embed invisible instructions in emails that trick your AI into including sensitive information in its drafted responses or forwarding data to external addresses.",
    mitigation:
      "Use the dual LLM pattern: have a privileged AI coordinate with a quarantined AI that processes untrusted content, never exposing your main system to raw external input.",
  },
  {
    title: "MCP GitHub Exfiltration",
    scenario:
      "You're using an AI with GitHub integration to help review pull requests. A contributor submits a PR updating the README with improved installation instructions.",
    risk: "The hidden whitespace contains instructions to search your private repos for API keys and secrets, then hide them in the PR comments using zero-width characters.",
    mitigation:
      "Implement the action-selector pattern: let AI choose actions but never expose it to the responses from those actions, preventing malicious content from influencing subsequent operations.",
  },
];

// Format scenarios as context for the Q&A app
export function getScenariosAsContext(): string {
  return AI_HARM_SCENARIOS.map(scenario => 
    `**${scenario.title}**\nScenario: ${scenario.scenario}\nRisk: ${scenario.risk}\nMitigation: ${scenario.mitigation}`
  ).join('\n\n');
}

// Compressed version for token-limited contexts
export function getCompressedScenariosContext(): string {
  const compressed = `AI Risk Examples:
- Deepfake video calls for financial theft - verify via multiple channels
- Hidden prompt injections in emails - isolate AI from untrusted content  
- Poisoned training data - audit data sources regularly
- Malicious model files - scan before loading
- Exposed AI servers - require authentication
- System prompt leaks - separate user/system instructions
- Copyright reproduction - use differential privacy
- Deepfake job candidates - multi-step verification
- AI validating self-harm - redirect to crisis resources
- AI reinforcing delusions - avoid validating false beliefs
- Hallucinated medical advice - verify with professionals
- Phishing links from AI - manually verify URLs
- False defamation in summaries - fact-check outputs
- Academic dependency - maintain cognitive engagement
- Biased hiring algorithms - regular bias audits
- Manipulative AI companions - supervise usage
- Lethal trifecta (data+web+email access) - never combine all three
- Tool permission creep - audit regularly
- Invisible email instructions - use dual LLM pattern
- GitHub PR exfiltration - use action-selector pattern`;
  
  return compressed;
}